import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle, GraduationCap } from "lucide-react";
import { formatApiError } from "../../contexts/AuthContext";
import { useLang, fmtMoney } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

export const ScholarshipFinder = () => {
  const lang = useLang();
  const [options, setOptions] = useState(null);
  const [country, setCountry] = useState("");
  const [field, setField] = useState("");
  const [level, setLevel] = useState("");
  const [applying, setApplying] = useState(null);

  const load = () => {
    const params = new URLSearchParams({ type: "scholarship", ...(country && { country }) });
    axios.get(`${API}/funding/options`, { ...headers(), params }).then((r) => setOptions(r.data.options)).catch(() => setOptions([]));
  };
  useEffect(load, [country]);

  const apply = async (opt) => {
    setApplying(opt.id);
    try {
      await axios.post(`${API}/funding/apply`, { option_id: opt.id, amount: opt.max_amount, note: `${field} · ${level}` }, headers());
      toast.success(`Applied to ${opt.name} 🎓`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setApplying(null);
    }
  };

  if (options === null) return null;
  const filtered = options.filter((o) => (!field || o.fields?.includes(field) || !o.fields?.length) && (!level || o.levels?.includes(level) || !o.levels?.length));
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="scholarship-finder">
      <p className="flex items-center gap-2 font-display text-lg font-bold"><GraduationCap className="h-5 w-5 text-[#F5B300]" /> Scholarship Finder</p>
      <p className="mt-1 text-xs text-slate-500">Filtered by your country, field of study and level.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <select data-testid="sf-country" value={country} onChange={(e) => setCountry(e.target.value)}
          className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm text-white outline-none focus:border-[#7C3AED]/60">
          <option value="">All countries</option>
          <option value="India">India</option>
          <option value="Global">Global</option>
        </select>
        <select data-testid="sf-field" value={field} onChange={(e) => setField(e.target.value)}
          className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm text-white outline-none focus:border-[#7C3AED]/60">
          <option value="">All fields</option>
          <option value="culinary">Culinary</option>
          <option value="hospitality">Hospitality</option>
          <option value="business">Business</option>
          <option value="agriculture">Agriculture</option>
          <option value="entrepreneurship">Entrepreneurship</option>
        </select>
        <select data-testid="sf-level" value={level} onChange={(e) => setLevel(e.target.value)}
          className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm text-white outline-none focus:border-[#7C3AED]/60">
          <option value="">All levels</option>
          <option value="diploma">Diploma</option>
          <option value="undergraduate">Undergraduate</option>
          <option value="postgraduate">Postgraduate</option>
        </select>
      </div>
      {filtered.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">No scholarships match those filters yet.</p>
      ) : (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {filtered.map((o) => (
            <div key={o.id} data-testid={`sf-${o.id.slice(-6)}`} className="rounded-2xl border border-white/10 bg-[#09090B]/80 p-4">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-bold">{o.name}</p>
                <span className="rounded-full bg-[#22C55E]/10 px-2.5 py-1 font-jbmono text-[9px] font-bold text-[#22C55E]">{fmtMoney(o.max_amount, "INR", "₹")}</span>
              </div>
              <p className="mt-1 font-jbmono text-[10px] text-slate-500">{o.provider} · {o.country}</p>
              <p className="mt-1.5 text-xs text-slate-400">{o.blurb}</p>
              <button data-testid={`sf-apply-${o.id.slice(-6)}`} onClick={() => apply(o)} disabled={applying === o.id}
                className="mt-3 rounded-full bg-gradient-to-r from-[#F5B300] to-[#F97316] px-4 py-1.5 font-display text-[10px] font-bold uppercase text-[#09090B] disabled:opacity-50">
                {applying === o.id ? <LoaderCircle className="h-3 w-3 animate-spin" /> : "Apply"}
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export const BusinessCoach = () => {
  const [orgId, setOrgId] = useState(null);
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/org/mine`, headers()).then((r) => {
      const first = r.data.orgs?.[0];
      if (first) setOrgId(first.id);
    }).catch(() => {});
  }, []);

  const load = async (id) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/governance/coach/${id || orgId}`, headers());
      setCoach(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (orgId) load(orgId); }, [orgId]);

  if (!orgId) return null;
  return (
    <section className="rounded-3xl border border-[#F5B300]/20 bg-gradient-to-br from-[#F5B300]/[0.05] to-transparent p-6 backdrop-blur-xl" data-testid="business-coach">
      <div className="flex items-center justify-between">
        <p className="font-display text-lg font-bold">🤖 AI Business Coach</p>
        {coach && <span className="rounded-full bg-[#06B6D4]/10 px-3 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider text-[#06B6D4]" data-testid="coach-confidence">{coach.coach.confidence}% confidence</span>}
      </div>
      {loading ? (
        <LoaderCircle className="mt-6 h-5 w-5 animate-spin text-[#F5B300]" />
      ) : coach ? (
        <div className="mt-4 space-y-3">
          <p className="text-sm font-bold text-[#F5B300]" data-testid="coach-outlook">{coach.coach.outlook}</p>
          <div className="space-y-2">
            {coach.coach.actions.map((a, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-[#09090B]/80 p-3.5">
                <p className="text-xs font-bold">{a.title}</p>
                <p className="mt-0.5 text-xs text-slate-400">{a.detail}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500"><span className="font-bold text-[#EF4444]">Watch:</span> {coach.coach.risk}</p>
          <p className="font-jbmono text-[9px] uppercase tracking-widest text-slate-600">Traceable recommendation · logged for audit</p>
        </div>
      ) : null}
    </section>
  );
};
